import { useEffect, useRef, useState } from 'react';
import {
  GOOGLE_AD_SLOTS,
  GOOGLE_ADSENSE_CLIENT,
  GoogleAdPlacement,
  loadGoogleAdsScript,
} from '../../utils/google-ads';
import './style.css';

declare global {
  interface Window {
    adsbygoogle?: Record<string, unknown>[];
  }
}

interface Props {
  placement: GoogleAdPlacement;
  className?: string;
  onLoaded?: () => void;
  onUnfilled?: (reason: AdFailureReason) => void;
}

type AdState = 'idle' | 'loading' | 'filled' | 'unfilled';
type AdFailureReason = 'unfilled' | 'timeout' | 'request-error';

const reportedFailures = new Set<string>();

export default function GoogleAdSlot({
  placement,
  className = '',
  onLoaded,
  onUnfilled,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const adRef = useRef<HTMLModElement>(null);
  const onLoadedRef = useRef(onLoaded);
  const onUnfilledRef = useRef(onUnfilled);
  const [visible, setVisible] = useState(false);
  const [adState, setAdState] = useState<AdState>('idle');
  const slot = GOOGLE_AD_SLOTS[placement];

  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onUnfilledRef.current = onUnfilled;
  }, [onLoaded, onUnfilled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || visible) return;

    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    const ad = adRef.current;
    if (!visible || !slot || !ad || ad.dataset.adRequested === 'true') {
      return;
    }

    ad.dataset.adRequested = 'true';
    setAdState('loading');

    let active = true;
    let settled = false;
    let fallbackTimer = 0;

    const settle = (filled: boolean, reason?: AdFailureReason) => {
      if (!active || settled) return;
      settled = true;
      window.clearTimeout(fallbackTimer);
      setAdState(filled ? 'filled' : 'unfilled');

      if (filled) {
        onLoadedRef.current?.();
      } else {
        const failureReason = reason ?? 'unfilled';
        const failureKey = `${placement}:${failureReason}`;
        if (!reportedFailures.has(failureKey)) {
          reportedFailures.add(failureKey);
          console.warn(
            `[GoogleAds] O anúncio "${placement}" não foi preenchido (${failureReason}).`,
          );
        }
        onUnfilledRef.current?.(failureReason);
      }
    };

    const readAdStatus = () => {
      if (ad.dataset.adStatus === 'filled') settle(true);
      if (ad.dataset.adStatus === 'unfilled') settle(false, 'unfilled');
    };

    const observer = new MutationObserver(readAdStatus);
    observer.observe(ad, {
      attributes: true,
      attributeFilter: ['data-ad-status'],
    });

    loadGoogleAdsScript()
      .then(() => {
        if (!active) return;
        (window.adsbygoogle = window.adsbygoogle || []).push({});

        // O prazo começa depois que a solicitação é enviada. Antes, conexões
        // lentas podiam consumir todo o timeout apenas carregando o script.
        const responseTimeout = placement === 'feed' ? 30_000 : 10_000;
        fallbackTimer = window.setTimeout(
          () => settle(false, 'timeout'),
          responseTimeout,
        );
        readAdStatus();
      })
      .catch(error => {
        ad.dataset.adRequested = 'false';
        console.warn('[GoogleAds] Não foi possível solicitar o anúncio.', error);
        settle(false, 'request-error');
      });

    return () => {
      active = false;
      window.clearTimeout(fallbackTimer);
      observer.disconnect();
    };
  }, [slot, visible]);

  if (!slot) return null;

  const isLoading = adState !== 'filled' && adState !== 'unfilled';

  return (
    <div
      ref={containerRef}
      className={`google-ad google-ad--${placement} google-ad--${adState} ${className}`.trim()}
      aria-label={adState === 'unfilled' ? undefined : 'Publicidade'}
      aria-busy={isLoading}
      aria-hidden={adState === 'unfilled' ? true : undefined}
    >
      {adState !== 'unfilled' && (
        <span className="google-ad__label">Publicidade</span>
      )}
      {isLoading && placement === 'feed' && (
        <div className="google-ad__loading" aria-hidden="true">
          <span className="google-ad__loading-line" />
          <span className="google-ad__loading-line google-ad__loading-line--short" />
        </div>
      )}
      {visible && adState !== 'unfilled' && (
        <ins
          ref={adRef}
          className="adsbygoogle google-ad__unit"
          data-ad-client={GOOGLE_ADSENSE_CLIENT}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
          data-adtest={process.env.NODE_ENV !== 'production' ? 'on' : undefined}
        />
      )}
    </div>
  );
}
